import { X, ChevronLeft, ChevronRight, CheckCircle2, MapPin, Users, Calendar, Map, Link2, Sparkles, PartyPopper } from 'lucide-react';
import { type GuideState } from '../hooks/useSetupGuide';

type Tab = 'dashboard' | 'matrix' | 'team_timeline' | 'map' | 'team_conf' | 'points_conf' | 'categories_conf' | 'data_management' | 'appearance' | 'messages';

interface Step {
  emoji: string;
  icon: React.ElementType;
  title: string;
  subtitle: string;
  description: string;
  tip: string;
  ctaLabel: string;
  targetTab: Tab;
  color: string; // tailwind color stem
}

export const STEPS: Step[] = [
  {
    emoji: '📍',
    icon: MapPin,
    title: 'Créez votre premier Point',
    subtitle: 'Op Points',
    description:
      "Un point, c'est un endroit où votre équipe aura une mission ! Entrée VIP, scène, buvette, parking... Donnez-lui un nom, des horaires d'ouverture et le nombre de personnes nécessaires. Ajoutez une icône et une description pour que votre team sache exactement de quoi il s'agit. 🎯",
    tip: "💡 Vous pouvez créer autant de points que vous voulez pour couvrir toute votre zone !",
    ctaLabel: '→ Créer un Point',
    targetTab: 'points_conf',
    color: 'indigo',
  },
  {
    emoji: '👥',
    icon: Users,
    title: 'Ajoutez votre Team',
    subtitle: 'Manage Team',
    description:
      "Bienvenue dans votre équipe ! Ajoutez chaque membre : son nom, son email ou téléphone, sa couleur d'identification et sa photo. Chaque membre aura ensuite une application dédiée pour consulter ses shifts sur la map. 🤝",
    tip: "💡 Vous pourrez leur envoyer leur lien d'accès une fois la config terminée !",
    ctaLabel: '→ Ajouter du Team',
    targetTab: 'team_conf',
    color: 'emerald',
  },
  {
    emoji: '📅',
    icon: Calendar,
    title: 'Planifiez les Shifts',
    subtitle: 'Planning Matrix',
    description:
      "C'est l'heure d'organiser ! Dans la matrice de planning, sélectionnez un point et assignez vos membres d'équipe créneau par créneau. Utilisez le bouton Global Schedule à droite pour consulter le total d'heures par personne et équilibrer la charge. ⚡",
    tip: "💡 Cliquez sur une cellule vide pour ajouter un shift, sur une cellule remplie pour la modifier.",
    ctaLabel: '→ Ouvrir le Planning',
    targetTab: 'matrix',
    color: 'amber',
  },
  {
    emoji: '🗺️',
    icon: Map,
    title: 'Placez vos Points sur la Map',
    subtitle: 'Live Map',
    description:
      "Votre carte interactive vous attend ! Recherchez votre lieu via la barre de recherche en haut, puis cliquez sur l'onglet \"Placer un Point\" dans le panneau lateral pour positionner chacun de vos points à son emplacement réel. Votre team verra ces points en direct sur leur app. 📌",
    tip: "💡 Vous pouvez déplacer un point sur la map en le faisant glisser après l'avoir placé.",
    ctaLabel: '→ Ouvrir la Map',
    targetTab: 'map',
    color: 'sky',
  },
  {
    emoji: '🔗',
    icon: Link2,
    title: 'Partagez les Accès Team',
    subtitle: 'Manage Team',
    description:
      "Dernière étape ! Pour chaque membre, générez son lien d'accès personnel et envoyez-le via WhatsApp. Vous pouvez aussi imprimer les QR codes depuis Op Points pour que votre team puisse pointer sa présence directement sur place via leur application. 🎉",
    tip: "💡 Le QR code de pointage se trouve sur chaque carte de point dans la section Op Points.",
    ctaLabel: '→ Gérer les Accès',
    targetTab: 'team_conf',
    color: 'purple',
  },
];

const COLOR_MAP: Record<string, { bg: string; border: string; text: string; ring: string; badge: string }> = {
  indigo: { bg: 'bg-indigo-500/15', border: 'border-indigo-500/40', text: 'text-indigo-300', ring: 'ring-indigo-500/50', badge: 'bg-indigo-500' },
  emerald: { bg: 'bg-emerald-500/15', border: 'border-emerald-500/40', text: 'text-emerald-300', ring: 'ring-emerald-500/50', badge: 'bg-emerald-500' },
  amber: { bg: 'bg-amber-500/15', border: 'border-amber-500/40', text: 'text-amber-300', ring: 'ring-amber-500/50', badge: 'bg-amber-500' },
  sky: { bg: 'bg-sky-500/15', border: 'border-sky-500/40', text: 'text-sky-300', ring: 'ring-sky-500/50', badge: 'bg-sky-500' },
  purple: { bg: 'bg-purple-500/15', border: 'border-purple-500/40', text: 'text-purple-300', ring: 'ring-purple-500/50', badge: 'bg-purple-500' },
};

interface SetupGuideProps {
  state: GuideState;
  onClose: () => void;
  onNext: () => void;
  onPrev: () => void;
  onGoToStep: (i: number) => void;
  onVisitStep: (i: number) => void;
  onComplete: () => void;
  onNavigate: (tab: Tab) => void;
}

export function SetupGuide({
  state,
  onClose,
  onNext,
  onPrev,
  onGoToStep,
  onVisitStep,
  onComplete,
  onNavigate,
}: SetupGuideProps) {
  if (!state.open) return null;

  const { step, visited, completed } = state;
  const current = STEPS[step];
  const c = COLOR_MAP[current.color];
  const isLast = step === STEPS.length - 1;
  const allVisited = visited.length >= STEPS.length;

  const handleCTA = () => {
    onVisitStep(step);
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    if (isMobile) {
      // On mobile: close the guide (frees up screen), set flag to auto-open modal
      onClose();
      if (step === 0) sessionStorage.setItem('guide_auto_open', 'new-point');
      if (step === 1) sessionStorage.setItem('guide_auto_open', 'new-team');
    }
    onNavigate(current.targetTab);
    if (!isLast) onNext();
  };

  return (
    <>
      {/* Mobile overlay backdrop */}
      <div
        className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm md:hidden"
        onClick={onClose}
      />

      {/* Panel — desktop: left-aligned after sidebar | mobile: centered */}
      <div
        className={`
          fixed z-[70]
          /* Desktop: left panel after sidebar */
          md:left-64 md:top-0 md:bottom-0 md:w-[350px]
          /* Mobile: centered card */
          inset-x-4 top-[10%] bottom-[10%] rounded-2xl
          md:rounded-none md:rounded-r-2xl md:inset-x-auto
          bg-[#14161B] border border-[#2D3139] shadow-2xl
          flex flex-col overflow-hidden
        `}
        style={{ animation: 'guideSlideIn 0.3s cubic-bezier(0.22, 1, 0.36, 1)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#2D3139] flex-shrink-0 bg-[#0A0B0E]/60">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-amber-400" />
            <span className="text-xs font-bold text-slate-300 uppercase tracking-widest">Guide de Configuration</span>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-[#2D3139] transition-colors"
            title="Réduire"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Progress bar */}
        <div className="px-5 py-3 flex-shrink-0">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Progression</span>
            <span className="text-[10px] font-bold text-slate-400">{visited.length}/{STEPS.length} étapes</span>
          </div>
          <div className="h-1.5 bg-[#2D3139] rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-indigo-500 via-emerald-500 to-amber-500 rounded-full transition-all duration-700"
              style={{ width: `${(visited.length / STEPS.length) * 100}%` }}
            />
          </div>
        </div>

        {/* Step dots navigation */}
        <div className="flex items-center justify-center gap-3 px-5 pb-3 flex-shrink-0">
          {STEPS.map((s, i) => {
            const isVisited = visited.includes(i);
            const isCurrent = i === step;
            const sc = COLOR_MAP[s.color];
            return (
              <button
                key={i}
                onClick={() => onGoToStep(i)}
                title={s.title}
                className={`flex items-center justify-center rounded-full transition-all ${
                  isVisited
                    ? `w-6 h-6 ${sc.badge} shadow-sm`
                    : isCurrent
                    ? `w-6 h-6 bg-[#2D3139] ring-2 ${sc.ring}`
                    : 'w-4 h-4 bg-[#2D3139] hover:bg-[#3D4149]'
                }`}
              >
                {isVisited ? (
                  <CheckCircle2 className="w-3.5 h-3.5 text-white" />
                ) : isCurrent ? (
                  <span className="text-[10px] font-bold text-white">{i + 1}</span>
                ) : null}
              </button>
            );
          })}
        </div>

        {/* Scrollable step content */}
        <div className="flex-1 overflow-y-auto custom-scrollbar">
          {completed ? (
            /* Completion screen */
            <div className="flex flex-col items-center justify-center h-full p-8 text-center">
              <div className="text-6xl mb-4 animate-bounce">🎉</div>
              <PartyPopper className="w-8 h-8 text-amber-400 mx-auto mb-3" />
              <h3 className="text-xl font-bold text-white mb-3">Configuration terminée !</h3>
              <p className="text-slate-400 text-sm leading-relaxed mb-6">
                Bravo ! Votre espace est prêt. Votre équipe peut maintenant accéder à leurs shifts, pointer leur présence et rester en contact avec vous. 🚀
              </p>
              <button
                onClick={onComplete}
                className="w-full py-3 bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-500 hover:to-emerald-400 text-white font-bold rounded-xl shadow-lg shadow-emerald-500/20 transition-all"
              >
                ✅ Terminer et fermer
              </button>
            </div>
          ) : (
            <div className="p-5 space-y-5">
              {/* Step header */}
              <div className={`rounded-2xl p-5 border ${c.bg} ${c.border}`}>
                <div className="text-4xl mb-3">{current.emoji}</div>
                <div className={`text-[10px] font-bold uppercase tracking-widest mb-1 ${c.text}`}>
                  Étape {step + 1} sur {STEPS.length} · {current.subtitle}
                </div>
                <h3 className="text-lg font-bold text-white leading-tight">{current.title}</h3>
              </div>

              {/* Description */}
              <div className="bg-[#0A0B0E] border border-[#2D3139] rounded-xl p-4">
                <p className="text-slate-300 text-sm leading-relaxed">{current.description}</p>
              </div>

              {/* Tip */}
              <div className="bg-amber-500/8 border border-amber-500/20 rounded-xl p-4">
                <p className="text-amber-300/90 text-xs leading-relaxed">{current.tip}</p>
              </div>

              {/* Visited badge */}
              {visited.includes(step) && (
                <div className="flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 rounded-xl px-4 py-3">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                  <span className="text-emerald-400 text-xs font-semibold">Étape complétée ✓</span>
                </div>
              )}

              {/* CTA Button */}
              <button
                onClick={handleCTA}
                className={`w-full py-3.5 font-bold text-white rounded-xl shadow-lg transition-all text-sm flex items-center justify-center gap-2 ${
                  visited.includes(step)
                    ? 'bg-[#1A1D23] border border-[#2D3139] hover:bg-[#22262E] text-slate-300'
                    : `bg-gradient-to-r from-${current.color}-600 to-${current.color}-500 hover:opacity-90 shadow-${current.color}-500/20`
                }`}
                style={!visited.includes(step) ? {
                  background: {
                    indigo: 'linear-gradient(to right, #4f46e5, #6366f1)',
                    emerald: 'linear-gradient(to right, #059669, #10b981)',
                    amber: 'linear-gradient(to right, #d97706, #f59e0b)',
                    sky: 'linear-gradient(to right, #0284c7, #0ea5e9)',
                    purple: 'linear-gradient(to right, #7c3aed, #a855f7)',
                  }[current.color]
                } : undefined}
              >
                {current.ctaLabel}
              </button>
            </div>
          )}
        </div>

        {/* Footer navigation */}
        {!completed && (
          <div className="flex items-center justify-between px-5 py-4 border-t border-[#2D3139] flex-shrink-0 bg-[#0A0B0E]/40">
            <button
              onClick={onPrev}
              disabled={step === 0}
              className="flex items-center gap-1.5 text-sm font-semibold text-slate-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors px-3 py-2 rounded-lg hover:bg-[#1A1D23]"
            >
              <ChevronLeft className="w-4 h-4" /> Précédent
            </button>

            {allVisited ? (
              <button
                onClick={onComplete}
                className="flex items-center gap-1.5 text-sm font-bold text-white bg-gradient-to-r from-emerald-600 to-emerald-500 hover:opacity-90 px-4 py-2 rounded-xl shadow-lg shadow-emerald-500/20 transition-all"
              >
                Terminer 🎉
              </button>
            ) : isLast ? (
              <span className="text-xs text-slate-500 italic">Visitez toutes les étapes</span>
            ) : (
              <button
                onClick={onNext}
                className="flex items-center gap-1.5 text-sm font-semibold text-slate-300 hover:text-white transition-colors px-3 py-2 rounded-lg hover:bg-[#1A1D23]"
              >
                Suivant <ChevronRight className="w-4 h-4" />
              </button>
            )}
          </div>
        )}
      </div>

      <style>{`
        @keyframes guideSlideIn {
          from { opacity: 0; transform: translateX(-20px); }
          to   { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </>
  );
}

/** Returns the targetTab for the given guide step index (used for sidebar highlight) */
export function getGuideTabForStep(step: number): string | null {
  return STEPS[step]?.targetTab ?? null;
}

/* ── Floating button shown when guide is closed but not completed ─── */
interface SetupGuideFloatButtonProps {
  step: number;
  total: number;
  onClick: () => void;
}
export function SetupGuideFloatButton({ step, total, onClick }: SetupGuideFloatButtonProps) {
  return (
    <button
      onClick={onClick}
      className="fixed bottom-6 left-[272px] z-50 flex items-center gap-2 bg-gradient-to-r from-indigo-600 to-indigo-500 hover:from-indigo-500 hover:to-indigo-400 text-white text-xs font-bold px-4 py-2.5 rounded-full shadow-xl shadow-indigo-500/30 transition-all hover:scale-105 md:flex hidden"
      style={{ animation: 'floatIn 0.4s cubic-bezier(0.22, 1, 0.36, 1)' }}
    >
      <Sparkles className="w-3.5 h-3.5" />
      Guide de config · {step + 1}/{total}
    </button>
  );
}

/* ── Welcome banner (shows on dashboard when no points) ──────────── */
interface SetupGuideBannerProps {
  onStart: () => void;
  categoryName?: string;
}
export function SetupGuideBanner({ onStart, categoryName }: SetupGuideBannerProps) {
  return (
    <div className="mx-4 md:mx-8 mt-6 relative overflow-hidden rounded-2xl border border-indigo-500/30 bg-gradient-to-br from-indigo-500/10 via-[#14161B] to-purple-500/10 p-6">
      {/* Decorative glow */}
      <div className="absolute -top-10 -right-10 w-40 h-40 bg-indigo-500/20 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-purple-500/15 rounded-full blur-2xl pointer-events-none" />

      <div className="relative z-10 flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <div className="text-5xl flex-shrink-0">👋</div>
        <div className="flex-1">
          <h3 className="text-lg font-bold text-white mb-1">
            Bienvenue{categoryName ? ` sur ${categoryName}` : ''} !
          </h3>
          <p className="text-slate-400 text-sm leading-relaxed max-w-lg">
            Vous n'avez pas encore configuré vos points opérationnels. Notre guide interactif vous accompagne pas à pas — de la création des points jusqu'au partage des accès de votre équipe. 🚀
          </p>
        </div>
        <button
          onClick={onStart}
          className="flex-shrink-0 flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-indigo-600 to-indigo-500 hover:from-indigo-500 hover:to-indigo-400 text-white font-bold rounded-xl shadow-lg shadow-indigo-500/30 transition-all hover:scale-105 whitespace-nowrap"
        >
          <Sparkles className="w-4 h-4" />
          Démarrer la configuration
        </button>
      </div>
    </div>
  );
}
